import {
    Select,
    Typography,
    Divider,
    Flex,
    Form,
    Button,
    message,
    Spin,
    notification,
} from "antd";
import {
    MailOutlined,
    PlusOutlined,
    ArrowLeftOutlined,
    CheckOutlined,
    WhatsAppOutlined,
    ShopOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { axiosInstance, displayEmail, generateRandomPassword } from "@/utilities";
import {
    API_BASE_URL,
    API_URL_PERMISSIONS_PARTICIPATION,
} from "@/services/XCONS_ENV";
import { debounce } from "@/utilities/debounce";
import { ContactResponse } from "@/types/schema/xconsTypess";
import { PermissionsCard } from "./forms/permissions/selected";
import { OperatorPermissionsCard } from "./forms/operators/selected";
import { NewContactCard } from "./forms/new-contact";
import { useNavigation } from "@refinedev/core";
import { CascaderProps } from "antd/lib/cascader";
import { DefaultOptionType } from "antd/es/select";
import { SellerIcon } from "../icon";
import { CustomerIcon } from "../icon/CustomerIcon";
import { t } from "i18next";
import { TwoPaneCascaderSearch } from "../cascader";
import { useContactConflictCheck } from "@/hooks/useContactConflictCheck";

const { Option } = Select;
const { Text } = Typography;

export type ContactOption = {
    role: string | undefined;
    id: number;
    contactId: number;
    name: string;
    firstname?: string;
    lastname?: string;
    email?: string;
    whatsapp?: string;
    type?: string;
    permissions?: {
        store?: { code: string; name?: string }[];
        seller?: { code: string; name?: string }[];
    };
};

type CascaderContactOption = DefaultOptionType & {
    _labelText?: string;
    _searchText?: string;
    _item?: ContactOption;
    _meta?: any;
    children?: CascaderContactOption[];
};

interface SmartSelectContactProps {
    handleCancel: () => void;
    onSaveContact: (data: any) => Promise<void>;
    store: { code: string; name: string; link: string } | undefined;
    refetch?: () => void;
    sectionType?: "permission" | "contact";
    contactId?: number;
    contactStores?: any;
    /**
     * Cómo se estira el panel. `drawer` (el default, y lo que había) reserva el alto de la
     * ventana para que la fila de acciones quede abajo de todo: es lo que necesita el cajón
     * lateral del Presupuesto. En un **modal** de 450px ese alto no tiene sentido —estira el
     * popup hasta que el botón Guardar queda abajo del fold y hay que scrollear la página— así
     * que ahí el panel crece con su contenido.
     */
    layout?: "drawer" | "modal";
    participants?: any;
    nextPosition?: number;
    permissionCode?: string;
}

export const SmartSelectContact = ({
    handleCancel,
    onSaveContact,
    store,
    refetch,
    sectionType = "permission",
    contactId,
    contactStores,
    layout = "drawer",
    participants,
    nextPosition,
    permissionCode = "contact",
}: SmartSelectContactProps) => {
    const [isNewUser, setIsNewUser] = useState(false);
    const [contacts, setContacts] = useState<ContactResponse[]>([]);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [selectOpen, setSelectOpen] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [permissionsSelects, setPermissionsSelects] = useState<any>();
    const [selectedContact, setSelectedContact] = useState<
        ContactOption | undefined
    >(undefined);
    const { push } = useNavigation();
    const [searchTerm, setSearchTerm] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [isCustomer, setIsCustomer] = useState(false);

    const [cascaderOpen, setCascaderOpen] = useState(false);
    const [form] = Form.useForm();

    const {
        runConflictCheck,
        applyConflictErrors,
        consumeBackendConflict,
    } = useContactConflictCheck(form);

    // Validates the new-contact form against /users/lookup whenever the
    // operator leaves the email or whatsapp field. Mirrors the on-blur flow
    // in `pages/contacts/contactsCreate.tsx`.
    const handleNewContactBlurValidate = async () => {
        const whatsapp = form.getFieldValue("whatsapp");
        const email = form.getFieldValue("email");
        const { whatsappError, emailError } = await runConflictCheck(
            whatsapp,
            email,
            fichaStoreCode(),
        );
        applyConflictErrors(whatsappError, emailError);
    };

    const handleAddContact = () => {
        setSelectOpen(false);
        setIsNewUser(true);
        setIsSelected(true);
        setSelectedContact(undefined);
        form.resetFields();
    };

    const resetFormState = () => {
        setSelectedContact(undefined);
        setPermissionsSelects(undefined);
        setIsNewUser(false);
        setIsSelected(false);
        form.resetFields();
        setLoadingSave(false);
    };

    const addedParticipationsSet = useMemo(() => {
        const keys = new Set<string>();
        (participants ?? []).forEach((p: any) => {
            if (p?.contactId) {
                const role = (p.participationType ?? "").trim() || "customer";
                const ident = (p.participationIdentifier ?? "").trim();
                keys.add(`${p.contactId}:${role}:${ident}`);
            }
        });
        return keys;
    }, [participants]);

    const normalizePermissionCodes = (permissions: any): Set<string> => {
        const set = new Set<string>();

        if (Array.isArray(permissions)) {
            for (const p of permissions) {
                const storeCode = String(p?.store ?? "").trim();
                const sellerCode = String(p?.seller ?? "").trim();
                if (storeCode) set.add(`store:${storeCode}`);
                if (sellerCode) set.add(`seller:${sellerCode}`);
            }
            return set;
        }

        const stores = permissions?.store;
        const sellers = permissions?.seller;

        if (Array.isArray(stores)) {
            for (const s of stores) {
                const code = String(s?.code ?? s ?? "").trim();
                if (code) set.add(`store:${code}`);
            }
        }
        if (Array.isArray(sellers)) {
            for (const s of sellers) {
                const code = String(s?.code ?? s ?? "").trim();
                if (code) set.add(`seller:${code}`);
            }
        }

        return set;
    };

    const filterPermissionsByStoreCode = (permissions: any, storeCode: string) => {
        if (!storeCode) return permissions;

        if (Array.isArray(permissions)) {
            return permissions.filter((p) => {
                const storeVal = String(p?.store ?? "").trim();
                const sellerVal = String(p?.seller ?? "").trim();
                return storeVal === storeCode || sellerVal === storeCode;
            });
        }

        if (permissions && typeof permissions === "object") {
            const stores = Array.isArray(permissions?.store) ? permissions.store : [];
            const sellers = Array.isArray(permissions?.seller) ? permissions.seller : [];

            const filteredStores = stores.filter((s: any) => String(s?.code ?? s ?? "").trim() === storeCode);
            const filteredSellers = sellers.filter((s: any) => String(s?.code ?? s ?? "").trim() === storeCode);

            return {
                ...permissions,
                store: filteredStores,
                seller: filteredSellers,
            };
        }

        return permissions;
    };

    /**
     * El store dentro del que se busca. `contactStores.storeCode` lo pasan los paneles que ya
     * trabajan sobre una ficha (relaciones, owner del presupuesto); los de **operadores** no lo
     * pasan, y sin scope `GET /contacts/search` devuelve **una fila por cada store** donde esa
     * persona sea contacto: buscando un e-mail en Puntoseco salían tres resultados idénticos
     * (fender, tecnopor, puntoseco) sin nada que los distinguiera.
     *
     * El código del contexto sólo sirve como scope cuando es un **store**: el de un seller no es un
     * store (los contactos son de stores; un seller se vincula a ellos, y sólo 32 de los 95 tienen
     * un store homónimo). Por eso los paneles de seller no mandan `storeCode` sino `sellerCode`,
     * que el backend traduce a los stores vinculados — sin ningún scope la búsqueda devolvía **una
     * fila por cada store** donde la persona tuviera ficha: 902 personas del padrón tienen ficha en
     * más de un store, y dos de ellas en 25.
     */
    const scopeStoreCode = () => {
        const fromPanel = String(contactStores?.storeCode ?? "").trim();
        if (fromPanel) return fromPanel;
        if (store?.link !== "store") return "";
        // El wrapper de la pantalla arma el código con `encodeURIComponent`.
        const raw = String(store?.code ?? "").trim();
        try {
            return decodeURIComponent(raw);
        } catch {
            return raw;
        }
    };

    /**
     * El seller dentro del que busca un panel de seller. No es un `storeCode` —los contactos son de
     * stores y el código de un seller no es uno de ellos—: el backend lo traduce a los stores
     * vinculados activos y colapsa la respuesta a una fila por persona. Nadie lo elige: es el
     * contexto del panel.
     */
    const scopeSellerCode = () => String(contactStores?.sellerCode ?? "").trim();

    /**
     * El store donde vive la **ficha** que se crea (alta de contacto nuevo, o el `POST /contacts`
     * del alta inline). No es lo mismo que `store.code`: en el panel de operadores del seller ese
     * código es el del **seller**, y usarlo como `storeCode` creaba fichas con un código que no es
     * ningún store (`contact.store_code = <seller>`). Sólo coincidían por casualidad en los stores
     * de un solo seller, donde `seller_code == store_code`. Los paneles de seller mandan el store
     * resuelto en `contactStores.fichaStoreCode`.
     */
    const fichaStoreCode = () =>
        scopeStoreCode() ||
        String(contactStores?.fichaStoreCode ?? "").trim() ||
        String(store?.code ?? "").trim();

    const getContacts = async (searchValue: string = "") => {
        if (loadingContacts) return;
        setLoadingContacts(true);
        const sCode = scopeStoreCode();
        const sellerScope = sCode ? "" : scopeSellerCode();

        try {
            const { data } = await axiosInstance.get(`${API_BASE_URL}/contacts/search`, {
                params: {
                    q: searchValue?.trim(),
                    limit: 60,
                    storeCode: sCode,
                    sellerCode: sellerScope,
                },
            });

            const incomingContacts: ContactResponse[] = Array.isArray(data) ? data : [];

            const filteredContacts =
                sectionType === "contact"
                    ? incomingContacts.filter((c) => {
                        if (c?.contactId === contactId) return false;

                        if (!sCode) return true;

                        const permsSet = normalizePermissionCodes((c as any)?.permissions);

                        const hasStore = permsSet.has(`store:${sCode}`);
                        const hasSeller = permsSet.has(`seller:${sCode}`);

                        if (hasStore || hasSeller) return true;

                        return String((c as any)?.storeCode ?? "").trim() === sCode;
                    })
                    : incomingContacts;

            const finalContacts = filteredContacts;

            setContacts(finalContacts);
        } catch (error) {
            console.error("Error al cargar contactos:", error);
        } finally {
            setLoadingContacts(false);
        }
    };

    const debouncedSearch = useMemo(
        () =>
            debounce(async (value: string) => {
                setSearchTerm(value);
                await getContacts(value);
            }, 1000),
        [],
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleSearch = (value: string) => {
        setInputValue(value);
        debouncedSearch(value);
    };

    const transformContactsToOptions = (
        list: ContactResponse[],
    ): ContactOption[] => {
        return list.map((c) => ({
            id: c.user.userId,
            contactId: c.contactId,
            name: `${c.user.firstname} ${c.user.lastname}`,
            firstname: c.user.firstname,
            lastname: c.user.lastname,
            email: c.user.email,
            whatsapp: c.user.whatsapp ?? "",
            type: c.storeCode,
            role: c.storeCode,
            permissions: c.permissions ?? undefined,
        }));
    };

    const contactOptionLabel = (item: ContactOption) => (
        <Flex vertical gap={4}>
            <Text strong style={{ fontSize: 16 }}>
                {item.name}
            </Text>
            <Flex gap={8} align="center">
                <MailOutlined style={{ fontSize: 11 }} />
                <Text type="secondary">{displayEmail(item.email) || "-"}</Text>
            </Flex>
            <Flex gap={8} align="center">
                <WhatsAppOutlined style={{ fontSize: 11 }} />
                <Text type="secondary">{item.whatsapp || "-"}</Text>
            </Flex>
        </Flex>
    );

    const roleRow = (storeLabel: string, roleText: string) => {
        const roleLower = roleText.toLowerCase();

        let Icon;
        if (roleLower.includes("seller")) {
            Icon = SellerIcon;
        } else if (roleLower.includes("customer")) {
            Icon = CustomerIcon;
        } else {
            Icon = ShopOutlined;
        }

        return (
            <Flex align="center" gap={6}>
                <Icon style={{ fontSize: 12 }} />
                <Text style={{ textTransform: "capitalize" }}>
                    {roleText}
                </Text>
            </Flex>
        );
    };

    const mapContactsToCascaderOptions = (
        list: ContactResponse[],
    ): CascaderContactOption[] => {
        const contactOptions = transformContactsToOptions(list);
        return contactOptions.map((item, idx) => {
            const c = list[idx];
            const children: CascaderContactOption[] = [];
            const seen = new Set<string>();
            const currentStore = contactStores?.storeCode;
            const rawPerms = (c as any)?.permissions;
            const permsArray: Array<{ seller?: string; store?: string }> =
                Array.isArray(rawPerms) ? rawPerms : [];

            // 1) por permisos
            for (const p of permsArray) {
                // Store: solo si coincide con la store actual
                if (p?.store && currentStore && p.store === currentStore) {
                    const key = `store:${currentStore}`;
                    if (!seen.has(key) && !addedParticipationsSet.has(`${c.contactId}:store:${currentStore}`)) {
                        seen.add(key);
                        children.push({
                            value: `store:${currentStore}`,
                            label: roleRow(currentStore, t("contacts.role.store_operator")),
                            _searchText: `${currentStore} store operator`.toLowerCase(),
                            _item: item,
                            _meta: { role: "store", code: currentStore },
                        });
                    }
                }

                // Seller: se agrega UNO POR CADA seller del array de permisos
                if (p?.seller) {
                    const sellerCode = String(p.seller).trim();
                    const key = `seller:${sellerCode}`;
                    if (!seen.has(key) && !addedParticipationsSet.has(`${c.contactId}:seller:${sellerCode}`)) {
                        seen.add(key);
                        children.push({
                            value: `seller:${sellerCode}`,
                            label: roleRow(sellerCode, `Seller ${sellerCode}`),
                            _searchText: `${sellerCode} seller operator`.toLowerCase(),
                            _item: item,
                            _meta: { role: "seller", code: sellerCode },
                        });
                    }
                }
            }

            // 2) Fallback: si el contacto no tiene permisos de operador (store/seller)
            // sobre el store actual, se ofrece como Customer. NO se infiere "Store Operator"
            // ni "Seller Operator" desde storeOwner/sellerCode — esos campos vienen del
            // contact_relation/metadata y no garantizan permisos reales sobre este store.
            if (children.length === 0) {
                if (!addedParticipationsSet.has(`${c.contactId}:customer:`)) {
                    children.push({
                        value: "customer",
                        label: roleRow("Customer", t("contacts.role.customer")),
                        _searchText: "customer",
                        _item: item,
                        _meta: { role: "customer", code: "" },
                    });
                }
            }

            return {
                value: item.id,
                label: contactOptionLabel(item),
                _labelText: `${item.firstname} ${item.lastname}`,
                _searchText: `${item.firstname} ${item.lastname} ${item.email ?? ""} ${item.whatsapp ?? ""} ${item.type ?? ""}`.toLowerCase(),
                _item: item,
                children,
            } as CascaderContactOption;
        });
    };

    const onCascaderChange: CascaderProps["onChange"] = (
        _valuePath,
        selected,
    ) => {
        const top = selected?.[0] as CascaderContactOption | undefined;
        const last = selected?.[selected.length - 1] as CascaderContactOption | undefined;

        if (last?.value === "__create__") {
            handleAddContact();
            return;
        }

        const item = top?._item;
        if (!item) {
            resetFormState();
            return;
        }

        const role = last?._meta?.role;
        const identifier = last?._meta?.code ?? item.type ?? "";

        setIsCustomer(role === "customer");
        if (role === "customer") {
            setPermissionsSelects(null);
        }

        setSelectedContact((prev) =>
            prev
                ? { ...prev, role, type: identifier }
                : { ...item, role, type: identifier },
        );

        setIsSelected(true);
        setIsNewUser(false);

        form.setFieldsValue({
            firstname: item.firstname,
            lastname: item.lastname,
            email: item.email,
            whatsapp: item.whatsapp,
            type: identifier,
        });
    };

    const cascaderOptions = useMemo(() => {
        const all = mapContactsToCascaderOptions(contacts);
        return all.filter((opt) => opt.children && opt.children.length > 0);
    }, [contacts, addedParticipationsSet, contactStores?.storeCode]);

    const onSaveContactBefore = async (newUserPayload: any) => {
        // Errors propagate so the caller can decide between pinning a
        // structured conflict (backend BadRequestException with field+code)
        // to the form vs. showing a generic notification.
        const response = await axiosInstance.post(`${API_BASE_URL}/users`, newUserPayload);
        return response.data;
    };

    /**
     * OpenSpec 024 — el nombre tipeado va en el alta de la FICHA, no sólo en la de la identidad.
     *
     * `POST /users` sólo usa el nombre en el INSERT de una cuenta nueva: si la persona ya existía
     * —el caso frecuente al agregar un operador— lo descarta, y sin `firstname`/`lastname` acá la
     * ficha nacía sin nombre propio y el `COALESCE` caía al de la identidad. El operador escribía
     * un nombre y aparecía otro.
     */
    const createContactForUser = async (args: {
        storeCode: string;
        userId: number;
        firstname?: string;
        lastname?: string;
        description?: string;
        isActive?: boolean;
        additionalData?: Record<string, any> | null;
    }) => {
        const payload = {
            storeCode: args.storeCode,
            userId: args.userId,
            firstname: args.firstname,
            lastname: args.lastname,
            description: args.description ?? "Loggued in store",
            isActive: args.isActive ?? true,
            additionalData: args.additionalData ?? {},
        };

        const res = await axiosInstance.post(`${API_BASE_URL}/contacts`, payload);
        return res.data;
    };

    const handleSaveOperator = async () => {
        if (!isNewUser && !selectedContact) return;

        if (isNewUser) {
            const peek = form.getFieldsValue();
            const { whatsappError, emailError } = await runConflictCheck(
                peek.whatsapp,
                peek.email,
                fichaStoreCode(),
            );
            if (whatsappError || emailError) {
                applyConflictErrors(whatsappError, emailError);
                return;
            }
        }

        setLoadingSave(true);

        try {
            const values = await form.validateFields();
            const commonPayload = {
                type: store?.link,
                identifier: store?.code,
            };

            if (isNewUser) {
                const newUserPayload = {
                    email: values.email,
                    password: generateRandomPassword(),
                    storeCode: fichaStoreCode(),
                    firstname: values.firstname,
                    lastname: values.lastname,
                    whatsapp: values.whatsapp,
                };

                const createdUser = await onSaveContactBefore(newUserPayload);
                if (!createdUser?.user?.userId) throw new Error("No se recibió ID del usuario creado");

                const userId = createdUser.user.userId;

                if (!fichaStoreCode()) throw new Error("No se recibió el store de la ficha");

                const createdContact = await createContactForUser({
                    storeCode: fichaStoreCode(),
                    userId,
                    firstname: values.firstname,
                    lastname: values.lastname,
                    description: "Loggued in store",
                    isActive: true,
                    additionalData: {},
                });

                if (values.selectedOperator) {
                    const res = await axiosInstance.get(`${API_BASE_URL}/permissions/${store?.link}/${store?.code}/users/${values.selectedOperator}`);
                    const copiedPermissions = res.data || {};
                    await axiosInstance.post(`${API_BASE_URL}/permissions`, {
                        ...commonPayload,
                        userId,
                        permissions: copiedPermissions.permissions,
                        configs: copiedPermissions.configs,
                    });
                    message.success("Operador agregado con permisos");
                } else {
                    await axiosInstance.post(`${API_BASE_URL}/permissions`, {
                        ...commonPayload,
                        userId,
                        permissions: [],
                        configs: [],
                    });
                    push(userId);
                    message.success("Operador agregado");
                }

                await onSaveContact({
                    ...createdUser,
                    contact: createdContact,
                });
            }

            if (selectedContact?.id && !isNewUser) {
                const userId = selectedContact.id;
                if (values.selectedOperator) {
                    const res = await axiosInstance.get(`${API_BASE_URL}/permissions/${store?.link}/${store?.code}/users/${values.selectedOperator}`);
                    const copiedPermissions = res.data || {};
                    await axiosInstance.post(`${API_BASE_URL}/permissions`, {
                        ...commonPayload,
                        userId,
                        permissions: copiedPermissions.permissions,
                        configs: copiedPermissions.configs,
                    });
                    message.success("Operador agregado con permisos");
                } else {
                    await axiosInstance.post(`${API_BASE_URL}/permissions`, {
                        ...commonPayload,
                        userId,
                        permissions: [],
                        configs: [],
                    });
                    message.success("Operador agregado");
                }
            }

            resetFormState();
            refetch?.();
        } catch (error: any) {
            if (consumeBackendConflict(error)) return;
            if (
                error?.response?.data?.message ===
                "An user with that whatsapp or email already exists"
            ) {
                notification.warning({
                    message: <strong>An operator is already registered</strong>,
                    description:
                        "Enter a different email address or whatsapp number or check if you have already created this operator.",
                    duration: 4.5,
                    placement: "top",
                    icon: (
                        <ExclamationCircleOutlined
                            style={{ color: "#FAAD14" }}
                        />
                    ),
                });
                return;
            }
            console.error("Error al guardar operador", error);
            message.error(error?.message || "No se pudo guardar el operador");
        } finally {
            setLoadingSave(false);
        }
    };

    const handleSaveParticipant = async () => {
        if (!isNewUser && !selectedContact) return;

        // Run the same /users/lookup-based conflict check that fires on blur,
        // covering races where the operator submitted before the on-blur
        // request completed.
        if (isNewUser) {
            const values = form.getFieldsValue();
            const { whatsappError, emailError } = await runConflictCheck(
                values.whatsapp,
                values.email,
                fichaStoreCode(),
            );
            if (whatsappError || emailError) {
                applyConflictErrors(whatsappError, emailError);
                return;
            }
        }

        setLoadingSave(true);

        try {
            let contactIdToUse: number | undefined = selectedContact?.contactId;
            let role = selectedContact?.role || "store";
            let identifier = selectedContact?.type ?? "";

            if (isNewUser) {
                const values = await form.validateFields();

                if (!fichaStoreCode()) {
                    throw new Error("Falta storeCode");
                }

                const createdUser = await onSaveContactBefore({
                    email: values.email,
                    password: generateRandomPassword(),
                    storeCode: fichaStoreCode(),
                    firstname: values.firstname,
                    lastname: values.lastname,
                    whatsapp: values.whatsapp,
                });

                if (!createdUser?.user?.userId) {
                    setLoadingSave(false);
                    return;
                }

                const createdContact = await createContactForUser({
                    storeCode: fichaStoreCode(),
                    userId: createdUser.user.userId,
                    firstname: values.firstname,
                    lastname: values.lastname,
                    description: "Loggued in store",
                    isActive: true,
                    additionalData: {},
                });

                contactIdToUse = createdContact?.contactId;
                if (!contactIdToUse) {
                    throw new Error("No se obtuvo contactId del contacto creado");
                }

                // A freshly created contact has no store/seller membership
                // yet, so it can only be added as a Customer participant.
                role = "customer";
                identifier = "";
            }

            const isCust = role === "customer";

            let positionToSave = 0;
            const filtered = (participants ?? []).filter(
                (p: any) =>
                    (p.participationType ?? "").trim() === (isCust ? "" : role) &&
                    (p.participationIdentifier ?? "").trim() === identifier,
            );

            if (filtered.length > 0) {
                const maxPosition = Math.max(
                    ...filtered.map((p: any, index: number) => {
                        if (typeof p.position === 'number') return p.position;
                        return index;
                    })
                );
                positionToSave = maxPosition + 1;
            }

            const payload = {
                contactId: contactIdToUse,
                permissionCode,
                entityId: contactId,
                participationType: isCust ? "" : role,
                participationIdentifier: identifier,
                // Customers default to 'editor' so they can edit the record they
                // were added to. The "general access level" of the quote/order
                // would be the source of truth in a fuller implementation, but
                // hardcoding 'editor' matches today's UX (operator adds customer
                // expecting them to be able to edit).
                value: isCust ? "editor" : permissionsSelects,
                position: positionToSave,
            };

            await axiosInstance.post(API_URL_PERMISSIONS_PARTICIPATION, payload);
            message.success("Participante agregado");
            resetFormState();
            refetch?.();
            handleCancel();
        } catch (error: any) {
            if (error?.errorFields) {
                // Form validation error — antd already shows inline messages.
                return;
            }
            // Backend BadRequestException with {code, field, message} for
            // identity conflicts that slipped past the on-blur lookup.
            if (consumeBackendConflict(error)) {
                return;
            }
            console.error("Error al guardar participante", error);
            if (
                error?.response?.data?.message ===
                "An user with that whatsapp or email already exists"
            ) {
                notification.warning({
                    message: <strong>An operator is already registered</strong>,
                    description:
                        "Enter a different email address or whatsapp number or check if you have already created this operator.",
                    duration: 4.5,
                    placement: "top",
                    icon: (
                        <ExclamationCircleOutlined
                            style={{ color: "#FAAD14" }}
                        />
                    ),
                });
            } else if (error.message === "You do not have permission") {
                message.error(t("insufficient.permissions"));
            } else {
                message.error("Error al agregar participante, intente de nuevo");
            }
        } finally {
            setLoadingSave(false);
        }
    };

    const filterOption = useCallback(
        (input: string, option?: { props: Record<string, any> }) => {
            if (!option) return false;
            const searchTextLower = input.toLowerCase().trim();
            if (!searchTextLower) return true;
            const optionData = option.props["data-search"] || "";
            const searchTerms = searchTextLower.split(/\s+/);
            return searchTerms.every((term) => optionData.toLowerCase().includes(term));
        },
        [],
    );

    const memoizedRenderContactOption = useCallback(
        (item: ContactOption) => (
            <Option
                key={item.id}
                value={item.id}
                label={item.name}
                data-search={`${item.name} ${item.firstname} ${item.lastname} ${item.email || ""} ${item.whatsapp || ""}`.toLowerCase()}
            >
                <Flex vertical gap={4}>
                    <Text strong style={{ fontSize: 16 }}>
                        {item.name}
                    </Text>
                    <Flex gap={8} align="center">
                        <MailOutlined style={{ fontSize: 11 }} />
                        <Text type="secondary">{displayEmail(item.email) || "-"}</Text>
                    </Flex>
                    <Flex gap={8} align="center">
                        <WhatsAppOutlined style={{ fontSize: 11 }} />
                        <Text type="secondary">{item.whatsapp || "-"}</Text>
                    </Flex>
                </Flex>
            </Option>
        ),
        [],
    );

    const contactOptionsList = useMemo(() => {
        return transformContactsToOptions(contacts);
    }, [contacts]);

    return (
        <Spin spinning={loadingSave} >
            <Flex
                vertical
                style={layout === "modal" ? undefined : { minHeight: 'calc(100vh - 108px)' }}
            >
                {sectionType === "permission" ? (
                    <Select
                        placeholder={t("contacts.groups.enter.email.WhatsApp")}
                        loading={loadingContacts}
                        showSearch
                        allowClear
                        onSearch={handleSearch}
                        searchValue={inputValue}
                        style={{ width: "100%" }}
                        open={selectOpen}
                        onDropdownVisibleChange={(open) => {
                            setSelectOpen(open);
                            if (open && contacts.length === 0) getContacts();
                        }}
                        onSelect={(value) => {
                            const contact = transformContactsToOptions(contacts).find((it) => it.id === Number(value));
                            if (contact) {
                                setSelectedContact(contact);
                                setIsSelected(true);
                                setIsNewUser(false);
                                form.setFieldsValue({
                                    firstname: contact.firstname,
                                    lastname: contact.lastname,
                                    email: contact.email,
                                    whatsapp: contact.whatsapp,
                                    type: contact.type,
                                });
                            }
                        }}
                        dropdownRender={(menu) => (
                            <>
                                {menu}
                                {loadingMore && (
                                    <div style={{ padding: 8, textAlign: "center" }}>
                                        <Spin size="small" />
                                    </div>
                                )}
                                {sectionType === "permission" && (
                                    <>
                                        <Divider style={{ margin: "8px 0" }} />
                                        <div>
                                            <Button size="small" type="link" icon={<PlusOutlined />} onClick={handleAddContact}>
                                                {t("button.createnewcontact")}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        optionLabelProp="label"
                        filterOption={filterOption}
                        notFoundContent={
                            loadingContacts ? (
                                <Flex justify="center" align="center" style={{ padding: 16 }}>
                                    <Spin size="small" />
                                </Flex>
                            ) : (
                                <div style={{ padding: 16, textAlign: "center" }}>
                                    {searchTerm
                                        ? `No hay operadores para "${searchTerm}"`
                                        : `No hay operadores disponibles`}
                                </div>
                            )
                        }
                        listHeight={300}
                        optionFilterProp="data-search"
                        virtual
                    >
                        {contactOptionsList.map(memoizedRenderContactOption)}
                    </Select>
                ) : isNewUser ? null : (
                    <TwoPaneCascaderSearch
                        placeholder={t("contacts.groups.enter.email.WhatsApp")}
                        options={cascaderOptions as any}
                        open={cascaderOpen}
                        onOpenChange={(open) => {
                            setCascaderOpen(open);
                            if (open && contacts.length === 0) getContacts();
                        }}
                        loading={loadingContacts}
                        searchTerm={searchTerm}
                        inputValue={inputValue}
                        setInputValue={setInputValue}
                        onSearch={(q) => {
                            setCascaderOpen(true);
                            handleSearch(q);
                        }}
                        onChange={(_path, selected) => {
                            onCascaderChange(_path as any, selected as any);
                        }}
                        allowClear
                        notFoundContent={
                            loadingContacts ? (
                                <Flex justify="center" align="center" style={{ padding: 16 }}>
                                    <Spin size="small" />
                                </Flex>
                            ) : (
                                <div style={{ padding: 16, textAlign: "center" }}>
                                    {searchTerm ? `No hay contactos encontrados para "${searchTerm}"` : `No hay contactos disponibles`}
                                </div>
                            )
                        }
                        footer={
                            <Button
                                size="small"
                                type="link"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setCascaderOpen(false);
                                    handleAddContact();
                                }}
                            >
                                {t("button.createnewcontact")}
                            </Button>
                        }
                    />
                )}

                <Form
                    layout="vertical"
                    style={{ width: "100%", marginTop: 20, marginBottom: 20 }}
                    form={form}
                >
                    {sectionType === "permission" && isSelected && (
                        <OperatorPermissionsCard
                            store={store}
                            selectedContact={selectedContact}
                            isNewUser={isNewUser}
                        />
                    )}

                    {sectionType === "contact" && isSelected && !isNewUser && (
                        <PermissionsCard
                            store={store}
                            isCustomer={isCustomer}
                            selectedContact={selectedContact}
                            permissionsSelects={permissionsSelects}
                            setPermissionsSelects={setPermissionsSelects}
                        />
                    )}

                    {sectionType === "contact" && isNewUser && (
                        <NewContactCard
                            disabled={loadingSave}
                            onBlurValidate={handleNewContactBlurValidate}
                        />
                    )}
                </Form>

                <Flex justify="space-between" gap={8} style={{ marginTop: "auto" }}>
                    <Button
                        type="default"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            handleCancel();
                            resetFormState();
                        }}
                    >
                        {t("buttons.back")}
                    </Button>
                    <Button
                        type="primary"
                        /* Sin `conflictChecking`: el blur del campo dispara antes que el click y
                           se perdía el primer click (antd ignora el click de un botón en `loading`).
                           El guardado corre el chequeo igual antes de crear. */
                        disabled={!isNewUser && !selectedContact}
                        loading={loadingSave}
                        icon={<CheckOutlined />} iconPosition="end"
                        onClick={sectionType === "permission" ? handleSaveOperator : handleSaveParticipant}
                    >
                        {t("button.save")}
                    </Button>
                </Flex>
            </Flex>
        </Spin>
    );
};
